import { useEffect, useState, type ReactNode } from 'react';
import { FONT_STACK } from '../theme.js';
import { channelColorOf } from '../channelColors.js';

/**
 * Il riquadro di un canale: il logo quando c'è, il **nome** su un fondo colorato quando manca.
 *
 * ## Tre casi, non due
 *
 * 1. **Nessun indirizzo** — il catalogo non dichiara un logo: il nome sul colore del canale.
 * 2. **Un indirizzo che carica** — il logo, e riempie il riquadro.
 * 3. **Un indirizzo che non carica** — ed è il caso che si dimentica. Un `<img>` con un indirizzo
 *    morto non resta vuoto: mostra l'icona dell'immagine rotta del browser, che su un televisore a
 *    tre metri è un rettangolo grigio con una crocetta. Su stage 28 canali su 61 non hanno logo e gli
 *    altri puntano a domini di terzi che possono sparire: è un caso ordinario, non un'eccezione.
 *
 * ## Il nome, non le iniziali
 *
 * Nel prototipo dentro il riquadro c'è il nome del canale scritto per intero, su un fondo che è
 * **suo**. Io ci avevo messo due iniziali — `IS` per «IT| Sky Sport Uno HD», `J` per «Juventus» — che
 * non identificano niente: su un catalogo vero ci sono venti canali che cominciano per «IT|». Due
 * lettere occupano lo stesso riquadro che potrebbe dire il nome, e valgono meno.
 *
 * ## `cover` sulla card grande, `contain` nella riga
 *
 * Sulla card dell'intestazione il logo **riempie tutto**: è la card del canale che si sta guardando,
 * e un marchio piccolo in mezzo a trecento pixel di vuoto non è una scelta, è uno spreco. Nella
 * colonna della griglia, dove i riquadri sono sedici uno sotto l'altro e larghi 104 px, vale
 * l'opposto: lì ritagliare taglierebbe via proprio la parte che rende un marchio riconoscibile.
 */

export interface ChannelLogoProps {
  readonly name: string;
  readonly logoUrl: string | null;
  readonly widthPx: number;
  readonly heightPx: number;
  readonly radiusPx?: number;
  /** Quanto è grande il nome quando il logo non c'è. 17 nella riga, 40 nella card grande. */
  readonly nameFontPx?: number;
  /**
   * Come sta l'immagine dentro il riquadro.
   *
   * `cover` riempie e ritaglia, `contain` entra tutta e lascia spazio intorno. Vedi sopra perché sono
   * due e non una.
   */
  readonly fit?: 'cover' | 'contain';
  /**
   * Da cosa si deriva il colore del fondo. L'**identificativo** del canale, non il nome: i nomi
   * cambiano con gli import, e un canale che cambia colore dopo un import fa dire «si è rotto».
   */
  readonly colorSeed?: string;
}

export function ChannelLogo({
  name,
  logoUrl,
  widthPx,
  heightPx,
  radiusPx = 10,
  nameFontPx = 17,
  fit = 'contain',
  colorSeed,
}: ChannelLogoProps): ReactNode {
  const [failed, setFailed] = useState(false);

  /*
    Il fallimento si azzera quando cambia l'indirizzo.

    Le righe della guida si riciclano mentre si scorre: senza questo, un canale il cui logo è morto
    marchierebbe come rotto anche il canale che riusa quella riga dopo. Il difetto si vedrebbe solo
    scorrendo, cioè quasi mai in sviluppo e sempre su un catalogo vero.
  */
  useEffect(() => {
    setFailed(false);
  }, [logoUrl]);

  if (logoUrl === null || failed) {
    const color = channelColorOf(colorSeed ?? name);
    return (
      <div
        data-channel-logo="name"
        style={{
          flex: 'none',
          width: widthPx,
          height: heightPx,
          borderRadius: radiusPx,
          background: color.background,
          color: color.foreground,
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          textAlign: 'center',
          padding: '0 6px',
          boxSizing: 'border-box',
          font: `800 ${nameFontPx}px ${FONT_STACK}`,
          letterSpacing: nameFontPx >= 32 ? '-.02em' : undefined,
          /*
            Il nome va a capo e poi si abbrevia.

            Su un riquadro di 104 px un nome come «IT| Sky Sport Uno HD [1080p]» non ci sta su nessun
            numero di righe: tre e poi i puntini è il compromesso che lascia leggibile la parte che
            distingue — che sta all'inizio, non in fondo.
          */
          display: '-webkit-box',
          WebkitLineClamp: 3,
          WebkitBoxOrient: 'vertical',
          lineHeight: 1.1,
          wordBreak: 'break-word',
        }}
      >
        {name}
      </div>
    );
  }

  return (
    <div
      data-channel-logo="image"
      style={{
        flex: 'none',
        width: widthPx,
        height: heightPx,
        borderRadius: radiusPx,
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <img
        src={logoUrl}
        alt=""
        onError={() => setFailed(true)}
        style={
          fit === 'cover'
            ? { width: '100%', height: '100%', objectFit: 'cover' }
            : { maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }
        }
        // Il pigro serve alla guida: su dodicimila righe le immagini fuori schermo non si chiedono.
        loading="lazy"
        decoding="async"
      />
    </div>
  );
}
